import { z } from 'zod';

import { isSupportDomain, SUPPORT_DOMAINS } from './support/domains';

const assistantRouteEvidenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('support'), domain: z.enum(SUPPORT_DOMAINS) }),
  z.object({
    kind: z.literal('workforce_read'),
    shape: z.enum(['scalar', 'breakdown']),
  }),
  // `topics ["all"]` is the aspect RANKING (breakdown-shaped); any named
  // topic list is scalars.
  z.object({
    kind: z.literal('sentiment_read'),
    shape: z.enum(['scalar', 'ranking']),
  }),
  z.object({
    kind: z.literal('postings_read'),
    shape: z.enum(['scalar', 'breakdown']),
  }),
  // Transitions has one shape: every call prints ranked flow blocks and the
  // result carries no total, so no argument answers with a single value.
  z.object({
    kind: z.literal('transitions_read'),
    shape: z.literal('flows'),
  }),
]);

export type AssistantRouteEvidence = z.infer<
  typeof assistantRouteEvidenceSchema
>;

/** Only project already-validated tool inputs; never publish their payloads. */
export const createAssistantRouteEvidence = (
  name: string,
  input: unknown
): AssistantRouteEvidence | undefined => {
  if (typeof input !== 'object' || input === null) return undefined;
  const record = input as Record<string, unknown>;
  if (
    name === 'get_support_answer' &&
    typeof record.domain === 'string' &&
    isSupportDomain(record.domain)
  ) {
    return { kind: 'support', domain: record.domain };
  }
  if (name === 'query_workforce_data') {
    // A growth ranking (`rank_by: 'growth_rate'`) carries no metrics and no
    // breakdowns of its own — the ranked segments ARE the breakdown.
    if (
      record.rank_by === 'growth_rate' ||
      (Array.isArray(record.breakdowns) && record.breakdowns.length > 0)
    ) {
      return { kind: 'workforce_read', shape: 'breakdown' };
    }
    if (Array.isArray(record.metrics) && record.metrics.length > 0) {
      return { kind: 'workforce_read', shape: 'scalar' };
    }
  }
  if (name === 'query_sentiment_data' && Array.isArray(record.topics)) {
    if (record.topics.includes('all')) {
      return { kind: 'sentiment_read', shape: 'ranking' };
    }
    if (record.topics.length > 0) {
      return { kind: 'sentiment_read', shape: 'scalar' };
    }
  }
  if (name === 'query_postings_data') {
    if (Array.isArray(record.breakdowns) && record.breakdowns.length > 0) {
      return { kind: 'postings_read', shape: 'breakdown' };
    }
    if (Array.isArray(record.metrics) && record.metrics.length > 0) {
      return { kind: 'postings_read', shape: 'scalar' };
    }
  }
  // The only required argument; no optional one changes the answer's shape.
  if (
    name === 'query_transitions_data' &&
    typeof record.filterArtifactId === 'string' &&
    record.filterArtifactId.length > 0
  ) {
    return { kind: 'transitions_read', shape: 'flows' };
  }
  return undefined;
};

export const parseAssistantRouteEvidence = (
  name: string,
  value: unknown
): AssistantRouteEvidence | undefined => {
  const parsed = assistantRouteEvidenceSchema.safeParse(value);
  if (!parsed.success) return undefined;
  const evidence = parsed.data;
  if (
    (name === 'get_support_answer' && evidence.kind === 'support') ||
    (name === 'query_workforce_data' && evidence.kind === 'workforce_read') ||
    (name === 'query_sentiment_data' && evidence.kind === 'sentiment_read') ||
    (name === 'query_postings_data' && evidence.kind === 'postings_read') ||
    (name === 'query_transitions_data' && evidence.kind === 'transitions_read')
  ) {
    return evidence;
  }
  return undefined;
};

/** Validate the envelope, not model inputs, artifact payloads or eval metadata. */
/** Tokens one model used across a step's calls. */
const llmUsageSchema = z.object({
  model: z.string(),
  calls: z.number(),
  inputTokens: z.number(),
  cachedInputTokens: z.number(),
  outputTokens: z.number(),
});

export type LlmUsage = z.infer<typeof llmUsageSchema>;

export const turnStepEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), content: z.string() }),
  z.object({ type: z.literal('status'), content: z.string() }),
  z.object({ type: z.literal('error'), content: z.string() }),
  z.object({
    type: z.literal('artifact'),
    id: z.string(),
    kind: z.enum(['filter_response', 'chart_config']),
    payload: z.unknown(),
  }),
  z.object({
    type: z.literal('tool_call'),
    id: z.string(),
    name: z.string(),
    input: z.unknown(),
  }),
  z.object({
    type: z.literal('tool_step'),
    name: z.string(),
    phase: z.enum(['start', 'end']),
    ok: z.boolean().optional(),
    // Invalid optional evidence must not discard a valid execution event.
    routeEvidence: z.unknown().optional(),
    /**
     * How the step CONCLUDED, for the server tools dash-meta classifies (see
     * `OUTCOME_TOOLS`). `refused` and `empty` are the two states `ok` reports
     * as `true` — the step ran and did not crash — and which the reasoning
     * trace therefore drew a success tick for.
     *
     * SAME STRIPPING RULE AS `digest` BELOW: undeclared here, it vanishes
     * between `safeParse` and `onEvent`, the trace falls back to `ok`, and
     * every refusal silently goes back to rendering as a success with no error
     * anywhere to explain it.
     *
     * `.catch(undefined)` on an UNKNOWN member, deliberately, and it is the one
     * place in this schema that degrades rather than rejects. dash-meta and
     * this bundle deploy independently, so a fifth outcome added server-side
     * would otherwise fail the whole `tool_step` branch, fire
     * `onInvalidEnvelope` and END THE TURN for every browser still on an older
     * bundle — a cosmetic glyph field taking the user's answer with it.
     * Falling back to `undefined` means the trace reads `ok` instead and
     * renders precisely as it did before this field existed. A WRONG TYPE
     * (`outcome: 3`) degrades the same way for the same reason.
     */
    outcome: z
      .enum(['answered', 'refused', 'empty', 'failed'])
      .optional()
      .catch(undefined),
    /**
     * A server tool's model-facing result text, on the `end` phase — the only
     * browser-visible trace of what a SERVER tool actually read (see the field's
     * docblock in dash-meta's `global-assistant-turn-step.dto.ts`).
     *
     * IT MUST BE DECLARED HERE TO EXIST. zod strips unknown keys, so an event
     * field this schema does not name is dropped silently between `safeParse`
     * and `onEvent` — the FE and every wire consumer would see a well-formed
     * event with the field simply absent, and a test asserting on it would fail
     * for a reason nothing in the server or the harness explains.
     */
    digest: z.string().optional(),
  }),
  z.object({
    type: z.literal('done'),
    /**
     * Tokens the step's model calls used, by model: the loop's own calls and
     * every server tool's. Only the browser-side harness reads it; the cost
     * itself is priced there, since model prices change.
     */
    usage: z.array(llmUsageSchema).optional().catch(undefined),
  }),
]);

export type TurnStepEvent = z.infer<typeof turnStepEventSchema>;
